import { hexToRGB } from "@shared/utils/misc";
import { css } from "styled-components";
import { newLookTypography as typography } from "@shared/common/Themes/typography";
import {
  AppThemeType,
  baseScrollbarStyle,
  zIndexes,
} from "@shared/common/Themes/AppTheme";

const space = [4, 8, 16, 32, 64, 128];
const isDarkMode = false;
// Colors
const black = "#000000";
// Navy ink, a step softer than the brand navy so text reads less black.
const ink = "#1E2554";
const frameBgColor = "#FFFFFF";
const layoutBgColor = "#FFFFFF";
const cardBgColor = "#FFFFFF";
const secondaryCardBgColor = "#FFFFFF";
const bgColor = cardBgColor;
const mediumBgColor = cardBgColor;
const lightBackground = "#F8F9FC";

const primaryBlue = "#3566D8";
const accentBlue = "#5890F9";
const midBlue = "#D7E3FF";
const lightBlue = "#EAF0FF";
const primaryGrey = "#293247";
const mediumGrey = "#4A5468";
const secondaryGrey = "#626B7A";
const lightestGrey = "#9AA3B5";
const midDark = secondaryGrey;
const borderBase = "#667085";

const primaryText = primaryGrey;
// Labels read in a firm grey; meta text (captions, timestamps) in mutedText.
const secondaryText = mediumGrey;
const mutedText = "#6B7385";
// The page canvas behind panels (white panels with hairline borders).
const canvasBgColor = "#FFFFFF";
const mediumText = mediumGrey;
const lightestText = lightestGrey;

const lightestBorderGrey = `${hexToRGB(borderBase, 0.12)}`;
const lightBorderGrey = `${hexToRGB(borderBase, 0.19)}`;
const mediumBorderGrey = `${hexToRGB(borderBase, 0.27)}`;
const hardBorderGrey = `${hexToRGB(borderBase, 0.45)}`;

const basic = primaryGrey;
const titleColor = ink;
const textColor = basic;
const buttonText = "#FFFFFF";

const layoutFooterColor = ink;
const titleCardBgColor = lightBlue;
const tableHoverBgColor = lightBackground;
const selectedTableColumnBgColor = "#F1F4FA";
const emptyColor = lightBackground;
const activeBgColor = "#FFFFFF";
const pieChartBorderColor = `${hexToRGB(secondaryGrey, 0.4)}`;
const hoverBgColor = lightestBorderGrey;
const chartTicksColor = `${hexToRGB(ink, 0.4)}`;
const chartGridColor = `${hexToRGB(borderBase, 0.16)}`;
const avatarBgColor = "#fEEAD9";
const avatarColor = "#F56A00";
const activeMenuItemBgColor = "#EEF3FF";
const dashboardCoinsBgColor = "#949BA80C";
const dashboardStatisticsBgColor = "#949BA80C";
const disabledInput = "#F3F5F9";
const inputBorderColor = "#D0D5DD";
const cardBorderColor = "#E7EAF0";
const tagBgColor = lightBlue;
const tvCurrentPriceColor = "#E3E9EC";
const boxInCardColor = lightBackground;
const highlightBgColor = lightBlue;
const subMenuTitleBgColor = "#E1F5FE40";
const checkboxBorderColor = "#D0D5DD";
const filterChipsColor = "#EEF1F7";
const filterChipsActiveColor = primaryGrey;
const bannerTextColor = ink;
const statisticsBgColor = "#F8F9FC";
const cardLightBgColor = "#F9FAFC";
const refreshSpinnerBgColor = "#FFFFFF";
const billingBgColor = "#FAFBFD";
const paymentBgColor = cardBgColor;

const blues = [lightBlue, midBlue, "#B5CBFB", "#8AAEF9", primaryBlue];
// Chart series: blue first, then colours that stay distinct next to it.
const pieChartColors = [
  primaryBlue,
  "#22B8CF",
  "#8B5CF6",
  "#F5A524",
  "#EC4899",
];
const demoTagColors = ["#9E32FE", "#0F54E9"];

//colors for old dark mode theme
const buttonBgColor = primaryBlue;
const graphColor = primaryBlue;
const secondaryIconBg = "#EDF4F7";

const colors = {
  // A calm green, about as light as the red, so profit reads without glare.
  green: "#169B62",
  lightGreen: "#75E3A1",
  darkGreen: "#0C7D3F",
  red: "#D9534F",
  secondaryRed: "#DB3328",
  darkRed: "#D4180C",
  orange: "#E79900",
  yellow: "#FEB932",
  darkYellow: "#9A6909",
  gold: "#F47601",
  lightGrey: "#E0E2E4",
  primary: primaryBlue,
  ink,
  accentBlue,
  white: "#FFFFFF",
  disabledText: hexToRGB(secondaryText, 0.5),
  black,
  purple: "#9E32FE",
  pink: "#EA32FE",
  primaryGrey,
  mediumGrey,
  secondaryGrey,
  lightBorderGrey,
  lightestBorderGrey,
  lightestGrey,
  primaryText,
  secondaryText,
  mutedText,
  canvasBgColor,
  lightestText,
  primaryBlue,
  blues,
  pieChartColors,
  cardBgColor,
  bgColor,
  layoutBgColor,
  frameBgColor,
  layoutFooterColor,
  titleColor,
  textColor,
  basic,
  buttonText,
  titleCardBgColor,
  tableHoverBgColor,
  selectedTableColumnBgColor,
  emptyColor,
  hoverBgColor,
  lightBackground,
  chartTicksColor,
  chartGridColor,
  avatarBgColor,
  avatarColor,
  activeMenuItemBgColor,
  pieChartBorderColor,
  mediumBorderGrey,
  hardBorderGrey,
  dashboardCoinsBgColor,
  dashboardStatisticsBgColor,
  disabledInput,
  midDark,
  tagBgColor,
  inputBorderColor,
  cardBorderColor,
  tvCurrentPriceColor,
  boxInCardColor,
  lightBlue,
  highlightBgColor,
  subMenuTitleBgColor,
  checkboxBorderColor,
  filterChipsColor,
  bannerTextColor,
  midBlue,
  statisticsBgColor,
  demoTagColors,
  cardLightBgColor,
  refreshSpinnerBgColor,
  filterChipsActiveColor,
  mediumText,
  billingBgColor,
  paymentBgColor,
  mediumBgColor,
  activeBgColor,
  secondaryCardBgColor,
  buttonBgColor,
  graphColor,
  secondaryIconBg,
};

const scrollbarStyle = css`
  ${baseScrollbarStyle};
  ul::-webkit-scrollbar-thumb,
  div::-webkit-scrollbar-thumb,
  &::-webkit-scrollbar-thumb {
    border: 3px solid transparent;
    background-color: #9aa3b599;
    border-radius: 100px;
    background-clip: content-box;
  }
`;

const shadows = {
  card: "0 1px 2px rgba(16, 24, 40, 0.03), 0 6px 18px rgba(16, 24, 40, 0.04)",
  hoverCard: "0 4px 16px rgba(16, 24, 40, 0.08)",
  symbolCard: "0 2px 8px rgba(16, 24, 40, 0.06)",
  // Panels sit on the canvas with a hairline and a faint contact shadow;
  // deeper shadows are for floating layers.
  dashboardCard:
    "0 1px 2px rgba(16, 24, 40, 0.04), 0 4px 16px rgba(16, 24, 40, 0.06)",
  // The card surface: borderless on the white page, the shadow alone
  // separates it from the canvas.
  panel: "0 1px 2px rgba(16, 24, 40, 0.04), 0 4px 16px rgba(16, 24, 40, 0.06)",
};

const borders = {
  lightest: `1px solid ${colors.lightestBorderGrey}`,
  light: `1px solid ${colors.lightBorderGrey}`,
  medium: `1px solid ${colors.mediumBorderGrey}`,
  hard: `1px solid ${hexToRGB(borderBase, 0.7)}`,
  card: `1px solid ${colors.cardBorderColor}`,
  // Card surfaces: a very thin hairline plus the soft shadow (as dark mode's
  // hairline), so cards keep a crisp edge on the page.
  panel: `1px solid ${colors.cardBorderColor}`,
};

// Panels 12, controls 8: precise rather than soft.
const radii = {
  control: 8,
  card: 12,
  pill: 999,
};

const gradients = {
  pageHaze: `radial-gradient(900px 320px at 88% -120px, ${hexToRGB(
    accentBlue,
    0.07
  )}, transparent 70%), radial-gradient(700px 260px at 15% -120px, ${hexToRGB(
    colors.purple,
    0.025
  )}, transparent 70%)`,
};

const NewLookTheme: AppThemeType = {
  isDarkMode,
  isNewLook: true,
  isPhone: false,
  hasNewLookBreakpoints: false,
  space,
  colors,
  zIndexes,
  typography,
  shadows,
  borders,
  radii,
  gradients,
  scrollbarStyle,
};

export default NewLookTheme;
